<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useUserStore } from './stores/userStore'
import { useBandStore } from './stores/bandStore'
import { useSongStore } from './stores/songStore'
import { useSetlistStore } from './stores/setlistStore'
import { useUiStore } from './stores/uiStore'
import { useConfigStore } from './stores/configStore'

// Components
import AuthView from './components/auth/AuthView.vue'
import UserHeader from './components/layout/UserHeader.vue'
import BandSelector from './components/bands/BandSelector.vue'
import BandCreateDialog from './components/bands/BandCreateDialog.vue'
import ConcurrencyDialog from './components/layout/ConcurrencyDialog.vue'
import InstallPrompt from './components/layout/InstallPrompt.vue'
import InviteUserDialog from './components/bands/InviteUserDialog.vue'
import CongratsDialog from './components/bands/CongratsDialog.vue'
import BandCard from './components/bands/BandCard.vue'

import api from './api'
import type { SongPartDto, SongSummaryDto, MixDto, SetlistSummaryDto } from './dtos'
import SongDetail from './components/bands/SongDetail.vue'
import SongPartDetail from './components/bands/SongPartDetail.vue'
import MixDetail from './components/bands/MixDetail.vue'
import SetlistDetail from './components/bands/SetlistDetail.vue'
import TeleprompterView from './components/bands/TeleprompterView.vue'
import UserProfile from './components/layout/UserProfile.vue'

const userStore = useUserStore()
const bandStore = useBandStore()
const songStore = useSongStore()
const setlistStore = useSetlistStore()
const uiStore = useUiStore()
const configStore = useConfigStore()
configStore.fetch()

// Screen Wake Lock — prevent screen from dimming while app is visible
let wakeLock: WakeLockSentinel | null = null
const requestWakeLock = async () => {
  try {
    wakeLock = await navigator.wakeLock.request('screen')
    wakeLock.addEventListener('release', () => { wakeLock = null })
  } catch { /* Wake Lock not supported or failed */ }
}
requestWakeLock()
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') requestWakeLock()
})

// Capture invite token from URL on load
const params = new URLSearchParams(window.location.search)
const inviteToken = params.get('invite')
if (inviteToken) {
  localStorage.setItem('pendingInviteToken', inviteToken)
  window.history.replaceState({}, '', window.location.pathname)
}

const pendingInviteBand = ref<string | null>(null)

// Peek at the pending invite to show banner on auth screen
const checkPendingInvite = async () => {
  const token = localStorage.getItem('pendingInviteToken')
  if (!token) return
  try {
    const response = await api.get(`/band-invite-links/peek/${token}`)
    pendingInviteBand.value = response.data.bandName
  } catch {
    localStorage.removeItem('pendingInviteToken')
  }
}
checkPendingInvite()

const user = computed(() => userStore.user)
const isVerifiedUser = computed(() => {
  const u = userStore.user
  if (!u) return false
  return u.emailVerified || u.providerData[0]?.providerId === 'google.com'
})
const dbUser = computed(() => userStore.dbUser)
const firebaseLoading = computed(() => userStore.loading)
const isSyncing = computed(() => userStore.isSyncing)
const isBandsLoading = computed(() => bandStore.isFetching)
const isSongsLoading = computed(() => songStore.isFetching)
const currentBand = computed(() => bandStore.currentBand)

const showCreateBand = ref(false)
const showInviteUser = ref(false)
const showCongrats = ref(false)
const joinedBandName = ref('')

// Global Loading state that doesn't unmount the dashboard
const showGlobalOverlay = computed(() => {
  return isSyncing.value || isBandsLoading.value || isSongsLoading.value || (isVerifiedUser.value && !dbUser.value)
})

// View State
const currentView = ref<'dashboard' | 'song' | 'songPart' | 'mix' | 'setlist' | 'teleprompter' | 'profile'>('dashboard')
const selectedSongId = ref<number | null>(null)
const selectedPart = ref<SongPartDto | null>(null)
const selectedMix = ref<MixDto | null>(null)
const selectedSetlistId = ref<number | null>(null)
const teleprompterSongId = ref<number | null>(null)

const handleViewSong = (song: SongSummaryDto) => {
  selectedSongId.value = song.id
  currentView.value = 'song'
}

const handleNavigateToSong = (bandId: number, songId: number) => {
  const band = bandStore.bands.find(b => b.id === bandId)
  if (band && bandStore.currentBand?.id !== bandId) {
    bandStore.setCurrentBand(band)
  }
  selectedSongId.value = songId
  currentView.value = 'song'
  nextTick(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }))
}

const handleNavigateToMix = async (bandId: number, songId: number, mixId: number) => {
  const band = bandStore.bands.find(b => b.id === bandId)
  if (band && bandStore.currentBand?.id !== bandId) {
    bandStore.setCurrentBand(band)
  }
  // Fetch both the mix DTO (header/dialog read title + rowVersion off props.mix)
  // and the song (MixDetail's header pulls song.title from songStore.currentSong)
  // so the entire mix view renders with correct labels regardless of how the
  // user got here (recent items / direct deep link / etc.).
  try {
    const token = await userStore.user?.getIdToken()
    if (!token) return
    const [mixResp] = await Promise.all([
      api.get(`/mixes/${mixId}`, { headers: { Authorization: `Bearer ${token}` } }),
      songStore.fetchSongDetails(songId),
    ])
    selectedSongId.value = songId
    selectedMix.value = mixResp.data
    currentView.value = 'mix'
    nextTick(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }))
  } catch (e) {
    console.error('Failed to load mix:', e)
  }
}

const handleNavigateToBand = (bandId: number) => {
  const band = bandStore.bands.find(b => b.id === bandId)
  if (band && bandStore.currentBand?.id !== bandId) {
    bandStore.setCurrentBand(band)
  }
  currentView.value = 'dashboard'
  nextTick(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }))
}

const handleViewPart = (part: SongPartDto) => {
  selectedPart.value = part
  currentView.value = 'songPart'
}

const handleViewMix = (mix: MixDto) => {
  selectedMix.value = mix
  currentView.value = 'mix'
}

const handleViewSetlist = (setlist: SetlistSummaryDto) => {
  selectedSetlistId.value = setlist.id
  currentView.value = 'setlist'
}

const teleprompterReturnView = ref<'dashboard' | 'setlist'>('setlist')

const handleOpenTeleprompter = (songId: number) => {
  teleprompterReturnView.value = currentView.value === 'setlist' ? 'setlist' : 'dashboard'
  teleprompterSongId.value = songId
  currentView.value = 'teleprompter'
}

const handleBackFromTeleprompter = () => {
  currentView.value = teleprompterReturnView.value
  teleprompterSongId.value = null
  songStore.currentSong = null
}

const handleBackToDashboard = () => {
  currentView.value = 'dashboard'
  selectedSongId.value = null
  selectedSetlistId.value = null
  songStore.clear()
  setlistStore.currentSetlist = null
  selectedPart.value = null
  selectedMix.value = null
}

const handleBackToSong = () => {
  currentView.value = 'song'
  selectedPart.value = null
  selectedMix.value = null
}

const previousView = ref<'dashboard' | 'song' | 'songPart' | 'mix' | 'setlist' | 'teleprompter'>('dashboard')

const handleOpenProfile = () => {
  if (currentView.value !== 'profile') {
    previousView.value = currentView.value as 'dashboard' | 'song' | 'songPart' | 'mix' | 'setlist' | 'teleprompter'
  }
  currentView.value = 'profile'
}

const handleBackFromProfile = () => {
  currentView.value = previousView.value
}

// --- Android back button support ---
// On PWAs the hardware/gesture back button defaults to closing the app
// because the SPA never pushes history entries. We track view changes
// and push a history state on each forward navigation, then route the
// resulting popstate event to the view's own in-app back handler.
let isHandlingPopState = false

const goBackWithinApp = () => {
  switch (currentView.value) {
    case 'songPart':
    case 'mix':
      handleBackToSong()
      return true
    case 'teleprompter':
      handleBackFromTeleprompter()
      return true
    case 'profile':
      handleBackFromProfile()
      return true
    case 'song':
    case 'setlist':
      handleBackToDashboard()
      return true
    default:
      return false
  }
}

const onPopState = () => {
  isHandlingPopState = true
  goBackWithinApp()
  isHandlingPopState = false
}

// Single back entry point for in-app Back buttons. Calling history.back()
// triggers popstate, which routes through goBackWithinApp — keeping the
// browser history in lockstep with our view state.
const goBack = () => history.back()

watch(currentView, (newView, oldView) => {
  if (isHandlingPopState) return
  if (newView === oldView) return
  // Only push when moving away from dashboard (the root). Moves between
  // non-dashboard views also push so back unwinds step by step.
  history.pushState({ view: newView }, '')
})

onMounted(() => {
  // Seed the initial history entry so popstate has something to fire on.
  history.replaceState({ view: 'dashboard' }, '')
  window.addEventListener('popstate', onPopState)
})

onUnmounted(() => {
  window.removeEventListener('popstate', onPopState)
})

const onInviteAccepted = (bandName: string) => {
  joinedBandName.value = bandName
  showCongrats.value = true
}

// Show congrats when user joins via invite link
watch(() => userStore.pendingJoinedBand, (bandName) => {
  if (bandName) {
    joinedBandName.value = bandName
    showCongrats.value = true
    userStore.pendingJoinedBand = null
  }
})
</script>

<template>
  <main class="min-h-screen bg-slate-200 flex flex-col items-center p-1 lg:p-6 font-sans relative overflow-x-hidden">
    
    <!-- Full-screen Loading Overlay (Overlay style, doesn't unmount content) -->
    <Transition name="fade">
      <div v-if="firebaseLoading || showGlobalOverlay" 
           class="absolute inset-0 z-[100] bg-slate-200/80 backdrop-blur-sm flex flex-col items-center justify-center text-slate-700">
        <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" fill="transparent" animationDuration=".5s" />
        <p class="mt-4 text-sm font-medium animate-pulse">Loading...</p>
      </div>
    </Transition>

    <!-- Teleprompter View (fullscreen overlay) -->
    <template v-if="user && currentView === 'teleprompter' && teleprompterSongId">
      <TeleprompterView :song-id="teleprompterSongId" @back="goBack" />
    </template>

    <!-- Mix View (full width on desktop, constrained on mobile/tablet) -->
    <template v-else-if="user && currentView === 'mix' && selectedMix">
      <div class="w-screen -mx-1 lg:-mx-6 -mt-1 lg:-mt-6">
        <UserHeader :user="user" :show-recent="false" @accepted="onInviteAccepted" @open-profile="handleOpenProfile" @navigate-to-song="handleNavigateToSong" @navigate-to-mix="handleNavigateToMix" @navigate-to-band="handleNavigateToBand" />
      </div>
      <div class="w-full max-w-md md:max-w-2xl lg:max-w-none lg:px-4">
        <div class="p-1 lg:p-4">
          <MixDetail :key="selectedMix.id" :mix="selectedMix" @back="goBack" />
        </div>
      </div>
    </template>

    <!-- Login View (centered, max-w-md always) -->
    <div v-else-if="(!user || !isVerifiedUser) && !firebaseLoading" class="flex-1 flex items-center justify-center w-full">
      <AuthView :pending-invite-band="pendingInviteBand" />
    </div>

    <!-- Setlist View -->
    <template v-else-if="user && currentView === 'setlist' && selectedSetlistId">
      <div class="w-screen -mx-1 lg:-mx-6 -mt-1 lg:-mt-6">
        <UserHeader :user="user" @accepted="onInviteAccepted" @open-profile="handleOpenProfile" @navigate-to-song="handleNavigateToSong" @navigate-to-mix="handleNavigateToMix" @navigate-to-band="handleNavigateToBand" />
      </div>
      <div class="w-full max-w-md md:max-w-2xl lg:max-w-4xl">
        <div class="p-1 lg:p-4">
          <SetlistDetail :setlist-id="selectedSetlistId" @back="goBack" @open-teleprompter="handleOpenTeleprompter" />
        </div>
      </div>
    </template>

    <!-- Song / Song Part View (full-width header like mix view) -->
    <template v-else-if="user && (currentView === 'song' || currentView === 'songPart')">
      <div class="w-screen -mx-1 lg:-mx-6 -mt-1 lg:-mt-6">
        <UserHeader :user="user" @accepted="onInviteAccepted" @open-profile="handleOpenProfile" @navigate-to-song="handleNavigateToSong" @navigate-to-mix="handleNavigateToMix" @navigate-to-band="handleNavigateToBand" />
      </div>
      <div class="w-full max-w-md md:max-w-2xl lg:max-w-4xl">
        <div class="p-1 lg:p-4">
          <div v-if="currentView === 'song' && selectedSongId">
            <SongDetail :key="selectedSongId" :song-id="selectedSongId" @back="goBack" @view-part="handleViewPart" @view-mix="handleViewMix" />
          </div>
          <div v-else-if="currentView === 'songPart' && selectedPart">
            <SongPartDetail :part="selectedPart" @back="goBack" />
          </div>
        </div>
      </div>
    </template>

    <!-- Profile -->
    <template v-else-if="user && currentView === 'profile'">
      <div class="w-screen -mx-1 lg:-mx-6 -mt-1 lg:-mt-6">
        <UserHeader :user="user" @accepted="onInviteAccepted" @open-profile="handleOpenProfile" @navigate-to-song="handleNavigateToSong" @navigate-to-mix="handleNavigateToMix" @navigate-to-band="handleNavigateToBand" />
      </div>
      <div class="w-full max-w-md md:max-w-2xl lg:max-w-4xl">
        <UserProfile @back="goBack" />
      </div>
    </template>

    <!-- Dashboard -->
    <template v-else-if="user">
      <div class="w-screen -mx-1 lg:-mx-6 -mt-1 lg:-mt-6">
        <UserHeader :user="user" @accepted="onInviteAccepted" @open-profile="handleOpenProfile" @navigate-to-song="handleNavigateToSong" @navigate-to-mix="handleNavigateToMix" @navigate-to-band="handleNavigateToBand" />
      </div>
      <div class="w-full max-w-md md:max-w-2xl lg:max-w-4xl">
        <div class="px-1 pb-1 pt-1 lg:px-4 lg:pb-4 lg:pt-1">
          <div v-if="currentView === 'dashboard'">
            <BandSelector @create-band="showCreateBand = true" />
            <BandCard v-if="currentBand" :band="currentBand" @invite-member="showInviteUser = true" @view-song="handleViewSong" @view-setlist="handleViewSetlist" @open-teleprompter="handleOpenTeleprompter" />
          </div>
        </div>
      </div>
    </template>

    <!-- Footer -->
    <footer class="w-screen -mx-1 lg:-mx-6 -mb-1 lg:-mb-6 mt-auto pt-4 pb-4 text-center text-xs text-slate-400 bg-slate-100 border-t border-slate-300">
      Copyright Riff Draft 2026
    </footer>

    <!-- Modals -->
    <BandCreateDialog v-model:visible="showCreateBand" />
    <InviteUserDialog v-model:visible="showInviteUser" />
    <CongratsDialog v-model:visible="showCongrats" :band-name="joinedBandName" />
    <ConcurrencyDialog />
    <InstallPrompt />

    <!-- Global Toasts -->
    <div class="fixed top-4 right-4 z-[200] flex flex-col gap-2 max-w-sm">
      <TransitionGroup name="toast">
        <div v-for="toast in uiStore.toasts" :key="toast.id"
          class="rounded-lg shadow-lg p-3 pr-8 text-sm relative cursor-pointer"
          :class="{
            'bg-red-600 border border-red-700 text-white': toast.severity === 'error',
            'bg-yellow-50 border border-yellow-200 text-yellow-800': toast.severity === 'warn',
            'bg-teal-50 border border-teal-200 text-teal-800': toast.severity === 'success',
            'bg-blue-50 border border-blue-200 text-blue-800': toast.severity === 'info'
          }"
          @click="uiStore.dismissToast(toast.id)"
        >
          <div class="font-semibold">{{ toast.summary }}</div>
          <div v-if="toast.detail" class="mt-1 text-xs opacity-80">{{ toast.detail }}</div>
          <button class="absolute top-2 right-2 opacity-50 hover:opacity-100" @click.stop="uiStore.dismissToast(toast.id)">
            <i class="pi pi-times text-xs"></i>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </main>
</template>

<style>
.font-sans {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.toast-enter-active {
  transition: all 0.3s ease;
}
.toast-leave-active {
  transition: all 0.2s ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
